import { Injectable } from '@angular/core';

const ACCESS_KEY = 'auth.access';
const REMEMBER_KEY = 'auth.remember';

/**
 * Service to manage authentication token storage across localStorage and sessionStorage.
 * Handles "Remember Me" functionality by switching storage mechanisms.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private storage: Storage = sessionStorage;

  /**
   * Sets the remember preference and switches storage mechanism accordingly.
   * @param remember If true, uses localStorage; if false, uses sessionStorage
   */
  setRemember(remember: boolean) {
    (remember ? localStorage : sessionStorage).setItem(REMEMBER_KEY, remember ? '1' : '0');
    this.storage = remember ? localStorage : sessionStorage;
  }

  /**
   * Gets the current remember preference.
   * @returns true if "Remember Me" is enabled
   */
  getRemember(): boolean {
    const v = localStorage.getItem(REMEMBER_KEY) ?? sessionStorage.getItem(REMEMBER_KEY);
    return v === '1';
  }

  /**
   * Saves the access token to the current storage mechanism (localStorage or sessionStorage).
   * @param token JWT access token to store
   */
  saveAccessToken(token: string) {
    this.storage.setItem(ACCESS_KEY, token);
  }

  /**
   * Retrieves the access token from storage (checks both localStorage and sessionStorage).
   * @returns The access token or null if not found
   */
  getAccessToken(): string | null {
    return (
      this.storage.getItem(ACCESS_KEY) ??
      localStorage.getItem(ACCESS_KEY) ??
      sessionStorage.getItem(ACCESS_KEY)
    );
  }

  /**
   * Clears all authentication data from both localStorage and sessionStorage.
   */
  clear() {
    [localStorage, sessionStorage].forEach((s) => {
      s.removeItem(ACCESS_KEY);
      s.removeItem(REMEMBER_KEY);
    });
  }
}
